import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { ToolId, ForeplayAd, Product } from '@/types'
import type { SelectionMode } from '@/components/reference-ads'

type EntryMode = 'existing' | 'new'

interface CompetitorResearchConfig {
  model: ToolId
  customPrompt: string
  aspectRatio: string
}

interface CompetitorResearchState {
  // Entry mode and step
  entryMode: EntryMode
  currentStep: number

  // Product state
  selectedProductId: string
  selectedProduct: Product | null
  productUrl: string

  // Keywords state
  keywords: string[]
  competitorBrands: string[]
  generatedDescription: string

  // Search results
  searchResults: { product_related: ForeplayAd[]; competitor_brands: ForeplayAd[] } | null
  selectedAdIds: string[]
  scoredAdsMap: Record<string, ForeplayAd>

  // Selection mode
  selectionMode: SelectionMode
  autoSelectCount: number

  // Collapsible sections
  expandProductRelated: boolean
  expandCompetitorBrands: boolean

  // Config
  config: CompetitorResearchConfig

  // Async operation status (persisted for page navigation)
  isSearching: boolean
  isScoring: boolean

  // Staleness tracking
  lastActiveAt: number

  // Actions
  setEntryMode: (mode: EntryMode) => void
  setCurrentStep: (step: number) => void
  setSelectedProductId: (id: string) => void
  setSelectedProduct: (product: Product | null) => void
  setProductUrl: (url: string) => void
  setKeywords: (keywords: string[]) => void
  setCompetitorBrands: (brands: string[]) => void
  setGeneratedDescription: (desc: string) => void
  setSearchResults: (results: { product_related: ForeplayAd[]; competitor_brands: ForeplayAd[] } | null) => void
  setSelectedAdIds: (ids: string[]) => void
  setScoredAdsMap: (map: Record<string, ForeplayAd>) => void
  setSelectionMode: (mode: SelectionMode) => void
  setAutoSelectCount: (count: number) => void
  setExpandProductRelated: (expanded: boolean) => void
  setExpandCompetitorBrands: (expanded: boolean) => void
  setConfig: (config: CompetitorResearchConfig) => void
  updateConfig: (updates: Partial<CompetitorResearchConfig>) => void
  setIsSearching: (isSearching: boolean) => void
  setIsScoring: (isScoring: boolean) => void

  // Reset
  reset: () => void
}

const initialState = {
  entryMode: 'existing' as EntryMode,
  currentStep: 1,
  selectedProductId: '',
  selectedProduct: null,
  productUrl: '',
  keywords: [],
  competitorBrands: [],
  generatedDescription: '',
  searchResults: null,
  selectedAdIds: [],
  scoredAdsMap: {},
  selectionMode: 'auto' as SelectionMode,
  autoSelectCount: 3,
  expandProductRelated: true,
  expandCompetitorBrands: true,
  config: {
    model: 'gemini_image' as ToolId,
    customPrompt: '',
    aspectRatio: '1:1',
  },
  isSearching: false,
  isScoring: false,
  lastActiveAt: 0,
}

export const useCompetitorResearchStore = create<CompetitorResearchState>()(
  persist(
    (set) => ({
      ...initialState,

      setEntryMode: (mode) => set({ entryMode: mode, lastActiveAt: Date.now() }),
      setCurrentStep: (step) => set({ currentStep: step, lastActiveAt: Date.now() }),
      setSelectedProductId: (id) => set({ selectedProductId: id, lastActiveAt: Date.now() }),
      setSelectedProduct: (product) => set({ selectedProduct: product, lastActiveAt: Date.now() }),
      setProductUrl: (url) => set({ productUrl: url, lastActiveAt: Date.now() }),
      setKeywords: (keywords) => set({ keywords, lastActiveAt: Date.now() }),
      setCompetitorBrands: (brands) => set({ competitorBrands: brands, lastActiveAt: Date.now() }),
      setGeneratedDescription: (desc) => set({ generatedDescription: desc, lastActiveAt: Date.now() }),
      setSearchResults: (results) => set({ searchResults: results, lastActiveAt: Date.now() }),
      setSelectedAdIds: (ids) => set({ selectedAdIds: ids, lastActiveAt: Date.now() }),
      setScoredAdsMap: (map) => set({ scoredAdsMap: map, lastActiveAt: Date.now() }),
      setSelectionMode: (mode) => set({ selectionMode: mode, lastActiveAt: Date.now() }),
      setAutoSelectCount: (count) => set({ autoSelectCount: count, lastActiveAt: Date.now() }),
      setExpandProductRelated: (expanded) => set({ expandProductRelated: expanded, lastActiveAt: Date.now() }),
      setExpandCompetitorBrands: (expanded) => set({ expandCompetitorBrands: expanded, lastActiveAt: Date.now() }),
      setConfig: (config) => set({ config, lastActiveAt: Date.now() }),
      updateConfig: (updates) => set((state) => ({ config: { ...state.config, ...updates }, lastActiveAt: Date.now() })),
      setIsSearching: (isSearching) => set({ isSearching, lastActiveAt: Date.now() }),
      setIsScoring: (isScoring) => set({ isScoring, lastActiveAt: Date.now() }),

      reset: () => set(initialState),
    }),
    {
      name: 'competitor-research-storage',
    }
  )
)
